import JSZip from 'jszip';

const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const EMU_PER_POINT = 12700;

function parseXml(xml, label) {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const errors = doc.getElementsByTagName('parsererror');
  if (errors.length > 0) {
    const detail = errors[0].textContent?.slice(0, 180) || 'unknown parse error';
    throw new Error(`Invalid ${label}: ${detail}`);
  }
  return doc;
}

async function readXml(zip, partPath, required = true) {
  const part = zip.file(partPath);
  if (!part) {
    if (!required) return null;
    throw new Error(`Template is missing ${partPath}`);
  }
  return parseXml(await part.async('string'), partPath);
}

function normalizePartPath(value) {
  const parts = [];
  String(value || '').split('/').forEach((part) => {
    if (!part || part === '.') return;
    if (part === '..') {
      parts.pop();
      return;
    }
    parts.push(part);
  });
  return parts.join('/');
}

function resolveTarget(sourcePartPath, target) {
  if (String(target || '').startsWith('/')) {
    return normalizePartPath(String(target).slice(1));
  }
  const sourceDirectory = sourcePartPath.slice(0, sourcePartPath.lastIndexOf('/') + 1);
  return normalizePartPath(`${sourceDirectory}${target}`);
}

function relationshipsPath(partPath) {
  const slash = partPath.lastIndexOf('/');
  const directory = partPath.slice(0, slash + 1);
  const fileName = partPath.slice(slash + 1);
  return `${directory}_rels/${fileName}.rels`;
}

async function readRelationships(zip, sourcePartPath) {
  const doc = await readXml(zip, relationshipsPath(sourcePartPath), false);
  const relationships = [];
  if (!doc) return relationships;

  const nodes = Array.from(doc.getElementsByTagNameNS('*', 'Relationship'));
  for (const node of nodes) {
    relationships.push({
      id: node.getAttribute('Id') || '',
      type: node.getAttribute('Type') || '',
      targetPath: resolveTarget(sourcePartPath, node.getAttribute('Target') || ''),
    });
  }
  return relationships;
}

function relationshipByType(relationships, suffix) {
  return relationships.find((relationship) => relationship.type.endsWith(`/${suffix}`)) || null;
}

function relationshipId(node) {
  return node.getAttributeNS(NS_REL, 'id') || node.getAttribute('r:id') || '';
}

function localElements(root, localName) {
  return Array.from(root.getElementsByTagNameNS('*', localName));
}

function firstLocalElement(root, localName) {
  return localElements(root, localName)[0] || null;
}

function toPoints(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.round((numeric / EMU_PER_POINT) * 100) / 100 : null;
}

function findAncestor(node, localName) {
  let current = node?.parentNode || null;
  while (current) {
    if (current.localName === localName) return current;
    current = current.parentNode;
  }
  return null;
}

function readTransform(shape) {
  const transform = firstLocalElement(shape, 'xfrm');
  const offset = transform ? firstLocalElement(transform, 'off') : null;
  const extent = transform ? firstLocalElement(transform, 'ext') : null;
  return {
    x: toPoints(offset?.getAttribute('x')),
    y: toPoints(offset?.getAttribute('y')),
    width: toPoints(extent?.getAttribute('cx')),
    height: toPoints(extent?.getAttribute('cy')),
  };
}

async function readLayoutMetadata(zip, layoutPath) {
  const doc = await readXml(zip, layoutPath);
  const root = doc.documentElement;
  const commonSlideData = firstLocalElement(root, 'cSld');
  const placeholders = localElements(root, 'ph').map((placeholder) => {
    const shape = findAncestor(placeholder, 'sp');
    const nonVisual = shape ? firstLocalElement(shape, 'cNvPr') : null;
    return {
      type: placeholder.getAttribute('type') || 'body',
      index: placeholder.getAttribute('idx') || null,
      name: nonVisual?.getAttribute('name') || null,
      ...readTransform(shape),
    };
  });
  const shapeTree = firstLocalElement(root, 'spTree');
  const shapeTypes = new Set(['sp', 'pic', 'graphicFrame', 'cxnSp', 'grpSp', 'contentPart']);
  const shapeCount = shapeTree
    ? Array.from(shapeTree.childNodes).filter((node) => node.nodeType === 1 && shapeTypes.has(node.localName)).length
    : 0;

  return {
    sourcePath: layoutPath,
    name: commonSlideData?.getAttribute('name') || layoutPath.split('/').pop(),
    type: root.getAttribute('type') || null,
    placeholders,
    shapeCount,
  };
}

async function readMasterMetadata(zip, masterPath) {
  const doc = await readXml(zip, masterPath);
  const commonSlideData = firstLocalElement(doc.documentElement, 'cSld');
  const shapeTree = firstLocalElement(doc.documentElement, 'spTree');
  const shapeTypes = new Set(['sp', 'pic', 'graphicFrame', 'cxnSp', 'grpSp', 'contentPart']);
  const shapeCount = shapeTree
    ? Array.from(shapeTree.childNodes).filter((node) => node.nodeType === 1 && shapeTypes.has(node.localName)).length
    : 0;
  return {
    sourcePath: masterPath,
    name: commonSlideData?.getAttribute('name') || masterPath.split('/').pop(),
    shapeCount,
  };
}

async function readThemeMetadata(zip, themePath) {
  if (!themePath) return null;
  const doc = await readXml(zip, themePath, false);
  if (!doc) return null;

  const colorScheme = firstLocalElement(doc.documentElement, 'clrScheme');
  const colors = {};
  if (colorScheme) {
    Array.from(colorScheme.childNodes).forEach((slot) => {
      if (slot.nodeType !== 1) return;
      const color = Array.from(slot.childNodes).find((node) => node.nodeType === 1);
      if (!color) return;
      colors[slot.localName] = color.getAttribute('lastClr') || color.getAttribute('val') || null;
    });
  }

  const fontScheme = firstLocalElement(doc.documentElement, 'fontScheme');
  const majorFont = fontScheme ? firstLocalElement(fontScheme, 'majorFont') : null;
  const minorFont = fontScheme ? firstLocalElement(fontScheme, 'minorFont') : null;
  return {
    name: doc.documentElement.getAttribute('name') || null,
    colorScheme: colorScheme?.getAttribute('name') || null,
    colors,
    fonts: {
      majorLatin: majorFont ? firstLocalElement(majorFont, 'latin')?.getAttribute('typeface') || null : null,
      minorLatin: minorFont ? firstLocalElement(minorFont, 'latin')?.getAttribute('typeface') || null : null,
    },
  };
}

function layoutPreference(layout) {
  const label = `${layout?.name || ''} ${layout?.type || ''}`.toLowerCase();
  if (/title\s*(and|&)\s*content|titleandcontent|obj/.test(label)) return 50;
  if (/title\s*only/.test(label)) return 40;
  if (/blank/.test(label)) return 30;
  if (/section/.test(label)) return -20;
  if (/title\s*slide|ctrtitle/.test(label)) return -30;
  return 0;
}

function choosePrimaryMaster(masters, slides) {
  return [...masters].sort((left, right) => {
    const leftUse = slides.filter((slide) => slide.masterPath === left.sourcePath).length;
    const rightUse = slides.filter((slide) => slide.masterPath === right.sourcePath).length;
    return rightUse - leftUse || left.order - right.order;
  })[0] || null;
}

function chooseCarrierLayout(layouts) {
  return [...layouts].sort((left, right) => (
    (right.useCount * 100 + layoutPreference(right)) -
    (left.useCount * 100 + layoutPreference(left)) ||
    left.order - right.order
  ))[0] || null;
}

export function computeTemplateFingerprint(base64) {
  const input = String(base64 || '');
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let i = 0; i < input.length; i++) {
    const code = input.charCodeAt(i);
    first ^= code;
    first = Math.imul(first, 0x01000193) >>> 0;
    second ^= code + i;
    second = Math.imul(second, 0x85ebca6b) >>> 0;
  }
  return `ppt-template-v1-${input.length}-${first.toString(16).padStart(8, '0')}${second.toString(16).padStart(8, '0')}`;
}

/**
 * Reads only design-system metadata from a PowerPoint package. Slide business
 * text is intentionally excluded so template examples never become content.
 */
export async function analyzePowerPointTemplate(base64) {
  if (typeof base64 !== 'string' || base64.trim().length === 0) {
    throw new Error('PowerPoint template data is empty');
  }

  let zip;
  try {
    zip = await JSZip.loadAsync(base64, { base64: true });
  } catch (error) {
    throw new Error(`PowerPoint template could not be opened: ${error.message}`);
  }

  const presentationPath = 'ppt/presentation.xml';
  const presentation = await readXml(zip, presentationPath);
  const presentationRelationships = await readRelationships(zip, presentationPath);
  const relationshipsById = new Map(presentationRelationships.map((item) => [item.id, item]));

  const slideSize = firstLocalElement(presentation.documentElement, 'sldSz');
  const slideNodes = localElements(presentation.documentElement, 'sldId');
  if (slideNodes.length === 0) {
    throw new Error('PowerPoint template has no source slides to carry its design');
  }

  const layoutCache = new Map();
  const masterCache = new Map();
  const slides = [];
  for (const [order, node] of slideNodes.entries()) {
    const sourceId = node.getAttribute('id') || '';
    const slideRelationship = relationshipsById.get(relationshipId(node));
    if (!sourceId || !slideRelationship?.targetPath) continue;

    const slideRelationships = await readRelationships(zip, slideRelationship.targetPath);
    const layoutRelationship = relationshipByType(slideRelationships, 'slideLayout');
    if (!layoutRelationship) continue;
    const layoutPath = layoutRelationship.targetPath;

    const layoutRelationships = await readRelationships(zip, layoutPath);
    const masterRelationship = relationshipByType(layoutRelationships, 'slideMaster');
    if (!masterRelationship) continue;
    const masterPath = masterRelationship.targetPath;

    if (!layoutCache.has(layoutPath)) {
      layoutCache.set(layoutPath, await readLayoutMetadata(zip, layoutPath));
    }
    if (!masterCache.has(masterPath)) {
      const master = await readMasterMetadata(zip, masterPath);
      masterCache.set(masterPath, { ...master, order: masterCache.size });
    }

    slides.push({
      order,
      sourceId,
      // Office insertSlidesFromBase64 expects the documented source ID form,
      // such as "256#", rather than the raw OOXML p:sldId value.
      officeSourceId: `${sourceId}#`,
      sourcePath: slideRelationship.targetPath,
      layoutPath,
      masterPath,
    });
  }

  if (slides.length === 0 || masterCache.size === 0) {
    throw new Error('PowerPoint template does not contain a usable slide master and layout');
  }

  const masters = [...masterCache.values()];
  const primaryMaster = choosePrimaryMaster(masters, slides);
  const masterRelationships = await readRelationships(zip, primaryMaster.sourcePath);
  const layoutRelationships = masterRelationships.filter((item) => item.type.endsWith('/slideLayout'));
  const layouts = [];
  for (const [order, relationship] of layoutRelationships.entries()) {
    let layout = layoutCache.get(relationship.targetPath);
    if (!layout) {
      layout = await readLayoutMetadata(zip, relationship.targetPath);
      layoutCache.set(relationship.targetPath, layout);
    }
    layouts.push({
      ...layout,
      order,
      useCount: slides.filter((slide) => slide.layoutPath === relationship.targetPath).length,
    });
  }

  const carrierLayout = chooseCarrierLayout(layouts.filter((layout) => layout.useCount > 0));
  const carrierSlide = slides.find((slide) => slide.layoutPath === carrierLayout?.sourcePath);
  if (!carrierLayout || !carrierSlide) {
    throw new Error('PowerPoint template does not contain a carrier slide for its primary master');
  }

  const themeRelationship = relationshipByType(masterRelationships, 'theme');
  const theme = await readThemeMetadata(zip, themeRelationship?.targetPath || null);
  const alternativeMasters = masters
    .filter((master) => master.sourcePath !== primaryMaster.sourcePath)
    .map((master) => ({
      name: master.name,
      sourcePath: master.sourcePath,
      slideCount: slides.filter((slide) => slide.masterPath === master.sourcePath).length,
    }));

  return {
    fingerprint: computeTemplateFingerprint(base64),
    sourceSlideCount: slideNodes.length,
    usableSourceSlideCount: slides.length,
    donorSlideId: carrierSlide.officeSourceId,
    dimensions: {
      width: toPoints(slideSize?.getAttribute('cx')),
      height: toPoints(slideSize?.getAttribute('cy')),
    },
    primaryMaster: {
      name: primaryMaster.name,
      sourcePath: primaryMaster.sourcePath,
      slideCount: slides.filter((slide) => slide.masterPath === primaryMaster.sourcePath).length,
      shapeCount: primaryMaster.shapeCount,
    },
    carrierLayout: {
      name: carrierLayout.name,
      type: carrierLayout.type,
      sourcePath: carrierLayout.sourcePath,
    },
    layouts: layouts.map(({ order, ...layout }) => layout),
    alternativeMasters,
    theme,
  };
}

/**
 * Build a carrier package whose source slides contain no example shapes. The
 * selected slide still points to its original layout, master, and theme, so
 * PowerPoint can import the design without ever placing donor content in the
 * destination deck, even briefly.
 */
export async function createPowerPointTemplateCarrier(base64) {
  let zip;
  try {
    zip = await JSZip.loadAsync(base64, { base64: true });
  } catch (error) {
    throw new Error(`PowerPoint template carrier could not be created: ${error.message}`);
  }

  const slidePaths = Object.keys(zip.files).filter((partPath) => (
    /^ppt\/slides\/slide\d+\.xml$/.test(partPath)
  ));
  for (const slidePath of slidePaths) {
    const doc = await readXml(zip, slidePath);
    const shapeTree = firstLocalElement(doc.documentElement, 'spTree');
    if (!shapeTree) {
      throw new Error(`Template carrier slide is missing its shape tree: ${slidePath}`);
    }
    for (const child of Array.from(shapeTree.childNodes)) {
      if (child.nodeType !== 1) continue;
      if (child.localName === 'nvGrpSpPr' || child.localName === 'grpSpPr') continue;
      shapeTree.removeChild(child);
    }

    const commonSlideData = firstLocalElement(doc.documentElement, 'cSld');
    if (!commonSlideData) {
      throw new Error(`Template carrier slide is missing common slide data: ${slidePath}`);
    }
    // Retain only the now-empty shape tree under cSld. Slide-level backgrounds,
    // controls, customer data, and extensions can carry relationship IDs and
    // are content rather than reusable master or layout design.
    for (const child of Array.from(commonSlideData.childNodes)) {
      if (child.nodeType === 1 && child !== shapeTree) commonSlideData.removeChild(child);
    }
    // The root needs only cSld and an optional color-map override. Transitions,
    // timing, controls, and extension data can reference removed relationships.
    for (const child of Array.from(doc.documentElement.childNodes)) {
      if (child.nodeType !== 1) continue;
      if (child === commonSlideData || child.localName === 'clrMapOvr') continue;
      doc.documentElement.removeChild(child);
    }
    zip.file(slidePath, new XMLSerializer().serializeToString(doc));

    const relsPath = relationshipsPath(slidePath);
    const relsPart = zip.file(relsPath);
    if (relsPart) {
      const relsDoc = await readXml(zip, relsPath);
      for (const relationship of Array.from(relsDoc.getElementsByTagNameNS('*', 'Relationship'))) {
        const type = relationship.getAttribute('Type') || '';
        if (!type.endsWith('/slideLayout')) relationship.parentNode?.removeChild(relationship);
      }
      zip.file(relsPath, new XMLSerializer().serializeToString(relsDoc));
    }
  }

  return zip.generateAsync({ type: 'base64' });
}

/**
 * A starter slide is removable only when it contains no user content. Empty
 * placeholders are safe; pictures, charts, groups, and custom shapes are not.
 */
export async function isBlankPowerPointSlideExport(base64) {
  if (typeof base64 !== 'string' || base64.length === 0) return false;
  let zip;
  try {
    zip = await JSZip.loadAsync(base64, { base64: true });
  } catch {
    return false;
  }
  const slidePath = Object.keys(zip.files)
    .filter((path) => /^ppt\/slides\/slide\d+\.xml$/.test(path))
    .sort()[0];
  if (!slidePath) return false;

  let doc;
  try {
    doc = await readXml(zip, slidePath);
  } catch {
    return false;
  }
  const shapeTree = firstLocalElement(doc.documentElement, 'spTree');
  if (!shapeTree) return true;

  const contentShapeTypes = new Set(['pic', 'graphicFrame', 'cxnSp', 'grpSp', 'contentPart']);
  for (const node of Array.from(shapeTree.childNodes)) {
    if (node.nodeType !== 1) continue;
    if (contentShapeTypes.has(node.localName)) return false;
    if (node.localName !== 'sp') continue;

    const isPlaceholder = localElements(node, 'ph').length > 0;
    const text = localElements(node, 't').map((item) => item.textContent || '').join('').trim();
    if (!isPlaceholder || text.length > 0) return false;
  }
  return true;
}
